import { useScrollPosition } from '@n8tb1t/use-scroll-position'
import {
  AppBar,
  Drawer,
  Icon,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  Underline,
} from '@pickupp/ui/core'
import { makeStyles, useTheme } from '@pickupp/ui/styles'
import { navigate } from '@reach/router'
import cx from 'classnames'
import PropTypes from 'prop-types'
import React, { useContext, useState } from 'react'

import { BROWSER, LANGUAGES, LOCALE_LOCAL_STORAGE, REGION } from '~/config'
import PickuppLogo from '~/src/assets/logo.png'
import Link from '~/src/components/Link'
import LinkButton from '~/src/components/LinkButton'
import { LocaleContext } from '~/src/contexts'
import { useIsMobile } from '~/src/hooks'

import styles from './styles'

const useStyles = makeStyles(styles)

const HeaderLogo = () => {
  const theme = useTheme()
  const classes = useStyles()

  return (
    <Link to="/" className={classes.imageContainer}>
      <img
        loading="lazy"
        className={classes.image}
        src={PickuppLogo}
        width={theme.global.logoWidth}
        alt="Pickupp"
      />
    </Link>
  )
}

const HeaderNavigation = ({ navigations }) => {
  const classes = useStyles()

  const { language } = useContext(LocaleContext)
  const isMobile = useIsMobile()

  // TODO: I think we can do better here haha
  const isSimilar = (v1, v2) => {
    return v1.replace(`/${language}/`, '').replace(/\//g, '') === v2.replace(/\//g, '')
  }

  return (
    <div className={classes.navigationContainer}>
      {navigations.map((nav) => (
        <Link
          key={nav.url}
          to={nav.url}
          target={nav.target}
          className={classes.navigationLink}
          activeClassName="active"
          event_name={nav.event_name}
        >
          <Typography variant="body2" weight={700} uppercase>
            {nav.text}
          </Typography>
          {BROWSER && !isMobile && isSimilar(window.location.pathname, nav.url) && (
            <Underline className={classes.underline} />
          )}
        </Link>
      ))}
    </div>
  )
}

HeaderNavigation.propTypes = {
  navigations: PropTypes.array.isRequired,
}

const HeaderLanguage = () => {
  const classes = useStyles()
  const [menuAnchor, setMenuAnchor] = useState(null)
  const { originalPath, label } = useContext(LocaleContext)

  const languages = LANGUAGES.filter((l) => {
    return !l.hide
  })

  if (languages.length === 1) return null

  const handleOpen = ({ currentTarget }) => {
    setMenuAnchor(currentTarget)
  }

  const handleClose = () => {
    setMenuAnchor(null)
  }

  const handleChangeLanguage = (key) => {
    // Sync local storage language
    window.localStorage.setItem(LOCALE_LOCAL_STORAGE, key)
    navigate(`/${key}${originalPath}`)
  }

  return (
    <>
      <div
        className={classes.languageContainer}
        onClick={handleOpen}
        style={{ cursor: 'pointer' }}
      >
        <Icon
          name="language"
          size="1.4rem"
          color="primary"
        />
        <Typography
          variant="body2"
          color="primary"
          weight={700}
          className={classes.languageText}
        >
          {label}
        </Typography>
      </div>
      <Menu
        keepMounted
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        getContentAnchorEl={null}
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={handleClose}
      >
        {languages.map(({ key, label: languageLabel }) => (
          <MenuItem
            key={key}
            onClick={() => {
              handleClose()
              handleChangeLanguage(key)
            }}
          >
            {languageLabel}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

const HeaderButtons = ({ loginButton, signUpButton }) => {
  const classes = useStyles()

  if (REGION === 'TW') return null

  return (
    <div className={cx(classes.navigationButton, classes.authButtons)}>
      <LinkButton
        className={classes.button}
        to={loginButton.url}
        target={loginButton.target}
        event_name={loginButton.event_name}
      >
        {loginButton.text}
      </LinkButton>
      <LinkButton
        className={classes.button}
        color="white"
        to={signUpButton.url}
        target={signUpButton.target}
        event_name={signUpButton.event_name}
      >
        {signUpButton.text}
      </LinkButton>
    </div>
  )
}

HeaderButtons.propTypes = {
  loginButton: PropTypes.object.isRequired,
  signUpButton: PropTypes.object.isRequired,
}

const Header = ({
  navigations,
  login_button,
  sign_up_button,
}) => {
  const [hide, setHide] = useState(false)
  useScrollPosition(({ prevPos, currPos }) => {
    const shouldHide = currPos.y < prevPos.y
    if (shouldHide !== hide) {
      setHide(shouldHide)
    }
  }, [hide])

  const [isOpen, setIsOpen] = useState(false)

  const classes = useStyles({ hide })

  return (
    <AppBar position="sticky" className={classes.appBar}>
      <Toolbar className={classes.toolbar}>
        <div className={classes.toolbarLeft}>
          <HeaderLogo />
          <HeaderNavigation navigations={navigations} />
        </div>
        <div className={classes.toolbarRight}>
          <HeaderLanguage />
          <HeaderButtons
            loginButton={login_button}
            signUpButton={sign_up_button}
          />
        </div>
      </Toolbar>
      <Toolbar className={classes.sideDrawer}>
        <HeaderLogo />
        <div className={classes.navigationButton}>
          <HeaderLanguage />
          <Icon
            name={isOpen ? 'close' : 'menu'}
            color="primary"
            className={classes.menuButton}
            onClick={() => setIsOpen(!isOpen)}
            disableRipple
            disableHover
          />
        </div>
        <Drawer
          anchor="right"
          open={isOpen}
          onClose={() => setIsOpen(false)}
          style={{
            zIndex: 5,
          }}
        >
          <div className={classes.drawerContainer}>
            <HeaderNavigation navigations={navigations} />
            <HeaderButtons
              loginButton={login_button}
              signUpButton={sign_up_button}
            />
          </div>
        </Drawer>
      </Toolbar>
    </AppBar>
  )
}

Header.propTypes = {
  navigations: PropTypes.array.isRequired,
  login_button: PropTypes.object.isRequired,
  sign_up_button: PropTypes.object.isRequired,
}

export default Header
